import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoStarOutline, IoStar, IoCloseOutline, IoCheckmarkCircle } from "react-icons/io5";
import { useCreateEvaluation } from "@/hooks/useEvaluations";
import { Spinner } from "../Spinner";
import toast from "react-hot-toast";

interface EvaluationModalProps {
  occurrenceId: string;
  occurrenceProtocol: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EvaluationModal = ({
  occurrenceId,
  occurrenceProtocol,
  isOpen,
  onClose,
}: EvaluationModalProps) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [problemSolved, setProblemSolved] = useState<boolean | undefined>(undefined);
  const [timeWasAdequate, setTimeWasAdequate] = useState<boolean | undefined>(undefined);
  const [communicationWasClear, setCommunicationWasClear] = useState<boolean | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);

  const createEvaluation = useCreateEvaluation();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Por favor, selecione uma nota");
      return;
    }

    try {
      await createEvaluation.mutateAsync({
        occurrenceId,
        rating,
        comment: comment.trim() || undefined,
        problemSolved: rating <= 3 ? problemSolved : undefined,
        timeWasAdequate: rating <= 3 ? timeWasAdequate : undefined,
        communicationWasClear: rating <= 3 ? communicationWasClear : undefined,
      });

      setSubmitted(true);
      toast.success("Avaliação enviada com sucesso!");

      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (error) {
      console.error("Erro ao enviar avaliação:", error);
      toast.error("Erro ao enviar avaliação. Tente novamente.");
    }
  };

  const resetForm = () => {
    setRating(0);
    setHoveredRating(0);
    setComment("");
    setProblemSolved(undefined);
    setTimeWasAdequate(undefined);
    setCommunicationWasClear(undefined);
    setSubmitted(false);
  };

  const handleClose = () => {
    if (!createEvaluation.isPending) {
      onClose();
      setTimeout(resetForm, 300);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        >
          {!submitted ? (
            <>
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Avalie o Atendimento
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Ocorrência #{occurrenceProtocol}
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={createEvaluation.isPending}
                  >
                    <IoCloseOutline className="text-2xl" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Rating Stars */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Como você avalia o atendimento? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2 justify-center py-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="transition-transform hover:scale-110 active:scale-95"
                        disabled={createEvaluation.isPending}
                      >
                        {star <= (hoveredRating || rating) ? (
                          <IoStar className="text-5xl text-yellow-400" />
                        ) : (
                          <IoStarOutline className="text-5xl text-gray-300" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 px-2">
                    <span>Péssimo</span>
                    <span>Excelente</span>
                  </div>
                </div>

                {/* Additional Questions (only for rating <= 3) */}
                {rating > 0 && rating <= 3 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-4 border-t border-gray-200 pt-4"
                  >
                    <p className="text-sm font-medium text-gray-700">
                      Para nos ajudar a melhorar:
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">O problema foi resolvido?</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setProblemSolved(true)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              problemSolved === true
                                ? "bg-green-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Sim
                          </button>
                          <button
                            onClick={() => setProblemSolved(false)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              problemSolved === false
                                ? "bg-red-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Não
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">O tempo foi adequado?</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setTimeWasAdequate(true)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              timeWasAdequate === true
                                ? "bg-green-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Sim
                          </button>
                          <button
                            onClick={() => setTimeWasAdequate(false)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              timeWasAdequate === false
                                ? "bg-red-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Não
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">A comunicação foi clara?</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCommunicationWasClear(true)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              communicationWasClear === true
                                ? "bg-green-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Sim
                          </button>
                          <button
                            onClick={() => setCommunicationWasClear(false)}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
                              communicationWasClear === false
                                ? "bg-red-500 text-white"
                                : "bg-white text-gray-600 border border-gray-300"
                            }`}
                            disabled={createEvaluation.isPending}
                          >
                            Não
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comentário (opcional)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Compartilhe mais detalhes sobre sua experiência..."
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    disabled={createEvaluation.isPending}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Seu feedback nos ajuda a melhorar nossos serviços
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  disabled={createEvaluation.isPending}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={rating === 0 || createEvaluation.isPending}
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {createEvaluation.isPending ? (
                    <>
                      <Spinner className="w-5 h-5" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar Avaliação"
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
              >
                <IoCheckmarkCircle className="text-6xl text-green-500 mx-auto mb-4" />
              </motion.div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Avaliação Enviada!
              </h3>
              <p className="text-gray-600">
                Obrigado pelo seu feedback. Ele é muito importante para melhorarmos nossos serviços.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
